import React from "react";
import * as actionTypes from "./actionTypes";
import { axiosInstance } from "../../components/Utils/API/axiosConfig";
import { Response } from "../../components/Utils/Models/api";
import { showErrorInterceptor, showLoadingComponent } from "./general_actions";
import * as Sentry from "@sentry/react";
import { Severity } from "@sentry/react";
import { firebaseApp, auth } from "../../components/Auth/firebase";
import {
  EmailAuthProvider,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  getIdTokenResult,
  applyActionCode,
  verifyPasswordResetCode,
  confirmPasswordReset,
  User,
  reauthenticateWithCredential,
} from "firebase/auth";
import { InternalAccountAssignedModel } from "../../components/Utils/Models/account";
import StoreStateModel from "../../components/Utils/Models/store";

const requestSignIn = () => {
  return {
    type: actionTypes.SIGNIN_REQUEST,
  };
};

const receiveSignIn = (decodedToken: any) => {
  return {
    type: actionTypes.SIGNIN_SUCCESS,
    decodedToken,
  };
};

const updateToken = (refreshToken: any) => {
  return {
    type: actionTypes.UPDATE_TOKEN,
    refreshToken,
  };
};

const signInError = (errorCode: string) => {
  return {
    type: actionTypes.SIGNIN_FAILURE,
    errorCode,
  };
};

const requestSignOut = () => {
  return {
    type: actionTypes.SIGNOUT_REQUEST,
  };
};

const receiveSignOut = () => {
  return {
    type: actionTypes.SIGNOUT_SUCCESS,
  };
};

const signOutError = (errorCode: string) => {
  return {
    type: actionTypes.SIGNOUT_FAILURE,
    errorCode,
  };
};

const verifyRequest = () => {
  return {
    type: actionTypes.VERIFY_REQUEST,
  };
};

const verifySuccess = () => {
  return {
    type: actionTypes.VERIFY_SUCCESS,
  };
};

export const reauthenticateCredentialRequest = () => {
  return {
    type: actionTypes.REAUTHENTICATE_CREDENTIAL_REQUEST,
  };
};

const reauthenticateCredentialSuccess = () => {
  return {
    type: actionTypes.REAUTHENTICATE_CREDENTIAL_SUCCESS,
  };
};

const reauthenticateCredentialFailure = (errorCode: string) => {
  return {
    type: actionTypes.REAUTHENTICATE_CREDENTIAL_FAILURE,
    errorCode,
  };
};

const updatePasswordRequest = () => {
  return {
    type: actionTypes.UPDATE_PASSWORD_REQUEST,
  };
};

const updatePasswordSuccess = () => {
  return {
    type: actionTypes.UPDATE_PASSWORD_SUCCESS,
  };
};

const updatePasswordFailure = (errorCode: string) => {
  return {
    type: actionTypes.UPDATE_PASSWORD_FAILURE,
    errorCode,
  };
};

const signUpRequest = () => {
  return {
    type: actionTypes.SIGNUP_REQUEST,
  };
};

const signUpSuccess = () => {
  return {
    type: actionTypes.SIGNUP_SUCCESS,
  };
};

const signUpFailure = (errorCode: string) => {
  return {
    type: actionTypes.SIGNUP_FAILURE,
    errorCode,
  };
};

const verifyEmailRequest = () => {
  return {
    type: actionTypes.VERIFY_EMAIL_REQUEST,
  };
};

const verifyEmailSuccess = () => {
  return {
    type: actionTypes.VERIFY_EMAIL_SUCCESS,
  };
};

const verifyEmailFailure = (errorCode: string) => {
  return {
    type: actionTypes.VERIFY_EMAIL_FAILURE,
    errorCode,
  };
};

const verifyPasswordResetCodeRequest = () => {
  return {
    type: actionTypes.VERIFY_PASSWORD_RESETCODE_REQUEST,
  };
};

const verifyPasswordResetCodeSuccess = (email: string) => {
  return {
    type: actionTypes.VERIFY_PASSWORD_RESETCODE_SUCCESS,
    email,
  };
};

const verifyPasswordResetCodeFailure = (errorCode: string) => {
  return {
    type: actionTypes.VERIFY_PASSWORD_RESETCODE_FAILURE,
    errorCode,
  };
};

const confirmPasswordResetRequest = () => {
  return {
    type: actionTypes.CONFIRM_PASSWORD_RESET_REQUEST,
  };
};

const confirmPasswordResetSuccess = () => {
  return {
    type: actionTypes.CONFIRM_PASSWORD_RESET_SUCCESS,
  };
};

const confirmPasswordResetFailure = (errorCode: string) => {
  return {
    type: actionTypes.CONFIRM_PASSWORD_RESET_FAILURE,
    errorCode,
  };
};

const sendVerificationEmailRequest = () => {
  return {
    type: actionTypes.SEND_VERIFICATION_EMAIL_REQUEST,
  };
};

const sendVerificationEmailSuccess = () => {
  return {
    type: actionTypes.SEND_VERIFICATION_EMAIL_SUCCESS,
  };
};

const sendVerificationEmailFailure = (errorCode: string) => {
  return {
    type: actionTypes.SEND_VERIFICATION_EMAIL_FAILURE,
    errorCode,
  };
};

export const updateAccountAssigned = (accountAssigned: InternalAccountAssignedModel) => {
  return {
    type: actionTypes.UPDATE_ACCOUNT_ASSIGNED,
    accountAssigned,
  };
};

// Dispatch section
///////////////////
export const getIdTokenResult1 = async (forceRefresh?: boolean) => {
  //https://firebase.google.com/docs/reference/js/firebase.auth.IDTokenResult#properties
  const token: any = getIdTokenResult(auth?.currentUser as User, forceRefresh).then(
    (token: any) => {
      return token;
    }
  );
  return token;
};

export const refreshToken = () => {
  return async (dispatch: any, getState: any) => {
    dispatch(showLoadingComponent(true));
    const refreshToken = await getIdTokenResult1(true);
    if (refreshToken) {
      dispatch(updateToken(refreshToken));
      dispatch(showLoadingComponent(false));
    } else {
      dispatch(showErrorInterceptor("SessionExpire"));
      dispatch(signOutUser());
    }
  };
};

export const signInUser = (email: string, password: string) => {
  return async (dispatch: any, getState: any) => {
    dispatch(requestSignIn());
    await signInWithEmailAndPassword(auth, email, password) //https://firebase.google.com/docs/auth/web/start#web-version-9
      .catch((error: any) => {
        dispatch(signInError(error.code));
        // saveTheSignInError(error); //Saved the error in the backend
      });
  };
};

export const verifyAuth = () => {
  return async (dispatch: any, getState: () => StoreStateModel) => {
    dispatch(verifyRequest());
    //onAuthStateChanged method which looks for a preexisting user session and re-establishes it. In our case this will happen on refresh. This method also sets up a listener while the app is running to change user session tokens when they expire.
    //https://firebase.google.com/docs/reference/android/com/google/firebase/auth/FirebaseAuth.AuthStateListener
    onAuthStateChanged(auth, async (user: any) => {
      if (user !== null) {
        const decodedToken = await user.getIdTokenResult().then((token: any) => {
          return token;
        });
        // const tempUser: any = JSON.parse(JSON.stringify(user))
        dispatch(receiveSignIn(decodedToken));
      }
      dispatch(verifySuccess());
    });
  };
};

export const signOutUser = () => {
  return (dispatch: any) => {
    dispatch(requestSignOut());
    signOut(auth)
      .then(() => {
        dispatch(receiveSignOut());
      })
      .catch((error: any) => {
        dispatch(signOutError(error.code));
      });
  };
};
////////////////////
//Axios interceptors

export const interceptors1 = (store: any) => {
  axiosInstance.interceptors.request.use(
    (conf: any) => {
      // I can attach the token here in each request but Im doing in the reducer
      // conf.headers['Auth'] = 'some token'

      //TODO: refresh token in each request if firebase dont handle it
      return conf;
    },
    (error: any) => {
      return Promise.reject(error);
    }
  );
  axiosInstance.interceptors.response.use(
    (response: Response) => {
      const data = response.data;
      if (data.hasErrors) {
        if (data.applicationEvents[0].code.length) {
          store.dispatch(showErrorInterceptor(data.applicationEvents[0].message));

          Sentry.captureMessage("HasError:" + data.applicationEvents[0].code, {
            level: Severity.Log,
            contexts: {
              errorInfo: {
                response: JSON.stringify(response),
                applicationEvents: JSON.stringify(data.applicationEvents),
              },
            },
          });
        } else {
          //show the "message" text, if "hasError" and the "code" is empty
          store.dispatch(showErrorInterceptor(data.applicationEvents[0].message));
          Sentry.captureMessage("HasError:" + data.applicationEvents[0].message, {
            level: Severity.Log,
            contexts: {
              errorInfo: {
                response: JSON.stringify(response),
                applicationEvents: JSON.stringify(data.applicationEvents),
              },
            },
          });
        }
      }
      return Promise.resolve(response);
    },
    (error: any) => {
      Sentry.captureMessage(error.response?.status || error.message, {
        level: Severity.Log,
        contexts: {
          errorInfo: {
            response: JSON.stringify(error.response),
          },
        },
      });
      if (error.response?.status === 401) {
        store.dispatch(signOutUser());
        store.dispatch(showErrorInterceptor("SessionExpire"));
      } else if (error.response?.status === 403) {
        store.dispatch(showErrorInterceptor("ForbiddenAccess"));
      } else if (error.response?.status === 404) {
        store.dispatch(showErrorInterceptor("ResourceNotFound"));
      } else if (error.response?.status >= 500) {
        store.dispatch(showErrorInterceptor("SystemError"));
      } else if (error.response?.status === 415) {
        store.dispatch(showErrorInterceptor("SystemError"));
      } else if (
        error.message === "CancelAPIRequest" ||
        error.code === "ERR_CANCELED" ||
        error.name === "CanceledError"
      ) {
        // This empty statement prevents from returning
        // 'Unknown Error' when canceling requests on account switch
        // intentionaly cancelled by Abortcontroller
      } else {
        if (error.response?.data) {
          store.dispatch(showErrorInterceptor(error.response?.data.applicationEvents[0].code));
        } else {
          store.dispatch(showErrorInterceptor("UnknownError"));
        }
      }
      return Promise.reject(error);
    }
  );
};
////////////////

export const updatePassword = (newPassword: string) => {
  return async (dispatch: any, getState: any) => {
    dispatch(updatePasswordRequest());
    const user: any = auth.currentUser;
    //https://firebase.google.com/docs/reference/js/firebase.User#updatepassword
    user
      .updatePassword(newPassword)
      .then((user: any) => {
        dispatch(updatePasswordSuccess());
      })
      .catch((error: any) => {
        dispatch(updatePasswordFailure(error.code));
      });
  };
};

export const verifyEmailUser = (oobCode: string, tenantId: string) => {
  return (dispatch: any) => {
    dispatch(verifyEmailRequest());
    if (tenantId && tenantId.length) {
      auth.tenantId = tenantId;
    }
    applyActionCode(auth, oobCode)
      .then(() => {
        dispatch(verifyEmailSuccess());
      })
      .catch((error: any) => {
        dispatch(verifyEmailFailure(error.code));
      });
  };
};

export const verifyPasswordResetCode1 = (oobCode: string, tenantId: string) => {
  return (dispatch: any) => {
    dispatch(verifyPasswordResetCodeRequest());
    if (tenantId && tenantId.length) {
      auth.tenantId = tenantId;
    }
    verifyPasswordResetCode(auth, oobCode)
      .then((email: string) => {
        dispatch(verifyPasswordResetCodeSuccess(email));
      })
      .catch((error: any) => {
        dispatch(verifyPasswordResetCodeFailure(error.code));
      });
  };
};

export const confirmPasswordReset1 = (oobCode: string, tenantId: string, newPassword: string) => {
  return (dispatch: any) => {
    dispatch(confirmPasswordResetRequest());
    if (tenantId && tenantId.length) {
      auth.tenantId = tenantId;
    }
    confirmPasswordReset(auth, oobCode, newPassword)
      .then(() => {
        dispatch(confirmPasswordResetSuccess());
      })
      .catch((error: any) => {
        dispatch(confirmPasswordResetFailure(error.code));
      });
  };
};

export const verifyPaswword = (password: string) => {
  return async (dispatch: any, getState: any) => {
    const user: any = auth.currentUser;
    const credential = EmailAuthProvider.credential(user.email, password);
    //https://firebase.google.com/docs/reference/js/firebase.User#reauthenticatewithcredential
    reauthenticateWithCredential(user, credential)
      .then(() => {
        dispatch(reauthenticateCredentialSuccess());
      })
      .catch((error) => {
        dispatch(reauthenticateCredentialFailure(error.message));
      });
  };
};
